import { useCallback, useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import {
  AVATARS_BY_ID,
  AVATAR_CATALOG,
  DICE_SKINS,
  type AiMatchPassStatus,
  type AvatarId,
  type DiceSkin,
  type EntitlementsView,
  type GameAction,
  type GameSettings,
  type PlayerProfile,
  type PublicRoomSummary,
  type RoomView,
  type ServerToClientEvents,
  type ClientToServerEvents,
  type SocketAck,
  type AppConfig,
} from '@shared/game/types';
import { HomeScreen } from './components/HomeScreen';
import { TutorialModal } from './components/TutorialModal';
import { LobbyScreen } from './components/LobbyScreen';
import { GameScreen } from './components/GameScreen';
import { RulesModal } from './components/RulesModal';
import { SettingsModal } from './components/SettingsModal';
import { ShopModal } from './components/ShopModal';
import { Toasts, type ToastItem } from './components/Toasts';
import {
  loadPreferences,
  loadSessionToken,
  savePreferences,
  saveSessionToken,
  type Preferences,
} from './lib/preferences';
import {
  playSound,
  setMusicVolume,
  setSfxVolume,
  startMusic,
  stopMusic,
  unlockAudio,
} from './lib/sound';
import {
  activateAiMatchPassTest,
  createAiMatchPassCheckout,
  createCheckout,
  getAiMatchPassStatus,
  getConfig,
  getEntitlements,
} from './lib/api';
import {
  applyAttributionToUrl,
  captureAttribution,
  getAttribution,
  type CtaLocation,
} from './lib/attribution';
import { track } from './lib/analytics';
import { copyToClipboard } from './lib/format';

type ClientSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

const FREE_AVATARS = AVATAR_CATALOG.filter((avatar) => !avatar.premium).map((avatar) => avatar.id);

function profileFromPreferences(preferences: Preferences): PlayerProfile {
  return {
    name: preferences.name.trim().slice(0, 20) || 'Viajero',
    avatar: preferences.avatar,
    diceSkin: preferences.diceSkin,
  };
}

function emitAck(socket: ClientSocket, event: string, payload?: unknown): Promise<SocketAck> {
  return new Promise((resolve) => {
    const callback = (result: SocketAck) => resolve(result);
    if (payload === undefined) {
      (socket.emit as (name: string, ack: (result: SocketAck) => void) => void)(event, callback);
    } else {
      (socket.emit as (name: string, body: unknown, ack: (result: SocketAck) => void) => void)(event, payload, callback);
    }
  });
}

function queryRoomCode(): string | null {
  try {
    const code = new URLSearchParams(window.location.search).get('sala');
    return code ? code.trim().toUpperCase().slice(0, 8) : null;
  } catch {
    return null;
  }
}

export default function App() {
  const [preferences, setPreferences] = useState<Preferences>(() => loadPreferences());
  const preferencesRef = useRef(preferences);
  preferencesRef.current = preferences;

  const [room, setRoom] = useState<RoomView | null>(null);
  const roomRef = useRef<RoomView | null>(null);
  roomRef.current = room;

  const [rooms, setRooms] = useState<PublicRoomSummary[]>([]);
  const [session, setSession] = useState<{ playerId: string; token: string } | null>(null);
  const sessionRef = useRef<{ playerId: string; token: string } | null>(null);
  sessionRef.current = session;

  const [connected, setConnected] = useState(false);
  const [busy, setBusy] = useState(false);
  const [action, setAction] = useState<GameAction | null>(null);
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [entitlements, setEntitlements] = useState<EntitlementsView>({
    products: [],
    premiumAvatars: [],
    premiumDice: [],
    cosmetics: [],
  });
  const [entitlementsLoaded, setEntitlementsLoaded] = useState(false);
  const [aiPassStatus, setAiPassStatus] = useState<AiMatchPassStatus | null>(null);
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const [checkoutBusy, setCheckoutBusy] = useState(false);

  const socketRef = useRef<ClientSocket | null>(null);
  const initialRoomRef = useRef<string | null>(queryRoomCode());
  const checkoutStateRef = useRef<string | null>(null);
  const checkoutHandledRef = useRef(false);
  const toastSeq = useRef(0);
  const lastToastRef = useRef<{ message: string; at: number }>({ message: '', at: 0 });

  const pushToast = useCallback((tone: ToastItem['tone'], message: string) => {
    const now = Date.now();
    if (lastToastRef.current.message === message && now - lastToastRef.current.at < 1600) return;
    lastToastRef.current = { message, at: now };
    toastSeq.current += 1;
    const id = toastSeq.current;
    setToasts((current) => [...current.slice(-3), { id, tone, message }]);
    window.setTimeout(() => {
      setToasts((current) => current.filter((toast) => toast.id !== id));
    }, 5200);
  }, []);

  const dismissToast = (id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  };

  const setRoomAndUrl = useCallback((next: RoomView | null) => {
    setRoom(next);
    roomRef.current = next;
    const url = new URL(window.location.href);
    if (next) url.searchParams.set('sala', next.code);
    else url.searchParams.delete('sala');
    if (url.toString() !== window.location.href) {
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    }
  }, []);

  const stripCheckoutParam = useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.delete('checkout');
    url.searchParams.delete('session_id');
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  }, []);

  const loadAiPassStatus = useCallback(async (token: string) => {
    try {
      setAiPassStatus(await getAiMatchPassStatus(token));
    } catch {
      setAiPassStatus(null);
    }
  }, []);

  const loadMyEntitlements = useCallback(async (token: string) => {
    try {
      const next = await getEntitlements(token);
      setEntitlements(next);
    } catch {
      // El perfil y la partida siguen funcionando sin cosméticos.
    } finally {
      setEntitlementsLoaded(true);
    }
  }, []);

  useEffect(() => {
    captureAttribution();
    try {
      checkoutStateRef.current = new URLSearchParams(window.location.search).get('checkout');
    } catch {
      checkoutStateRef.current = null;
    }
    getConfig()
      .then(setConfig)
      .catch(() => pushToast('warning', 'No se pudo leer la configuración del servidor.'));
  }, [pushToast]);

  useEffect(() => {
    const unlock = () => {
      unlockAudio();
      if (preferencesRef.current.music) startMusic(true, preferencesRef.current.musicVolume);
    };
    window.addEventListener('pointerdown', unlock, { once: true });
    return () => window.removeEventListener('pointerdown', unlock);
  }, []);

  useEffect(() => {
    if (window.localStorage.getItem('altia.tutorial.seen') === '1') return undefined;
    if (initialRoomRef.current) return undefined;
    const timer = window.setTimeout(() => setTutorialOpen(true), 900);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    setSfxVolume(preferences.sound ? preferences.sfxVolume : 0);
    setMusicVolume(preferences.musicVolume);
    if (preferences.music) startMusic(true, preferences.musicVolume);
    else stopMusic();
  }, [preferences.sound, preferences.sfxVolume, preferences.music, preferences.musicVolume]);

  useEffect(() => {
    savePreferences(preferences);
    if (!socketRef.current?.connected) return undefined;
    const timeout = window.setTimeout(() => {
      socketRef.current?.emit('profile:update', profileFromPreferences(preferences), () => undefined);
    }, 350);
    return () => window.clearTimeout(timeout);
  }, [preferences]);

  useEffect(() => {
    if (!entitlementsLoaded) return;
    const avatarDefinition = AVATARS_BY_ID[preferences.avatar];
    const diceIsPremium = DICE_SKINS.includes(preferences.diceSkin) &&
      ['aurora'].includes(preferences.diceSkin) &&
      !entitlements.premiumDice.includes(preferences.diceSkin);
    if ((avatarDefinition?.premium && !entitlements.premiumAvatars.includes(preferences.avatar)) || diceIsPremium) {
      const fallbackAvatar = FREE_AVATARS.includes(preferences.avatar) ? preferences.avatar : 'aurora';
      setPreferences((current) => ({
        ...current,
        avatar: fallbackAvatar as AvatarId,
        diceSkin: diceIsPremium ? 'classic' : current.diceSkin,
      }));
      pushToast('info', 'Se ha aplicado el aspecto gratuito: ese cosmético requiere el Pack Cosmético.');
    }
  }, [entitlementsLoaded, entitlements.premiumAvatars, entitlements.premiumDice, preferences.avatar, preferences.diceSkin, pushToast]);

  useEffect(() => {
    const socket: ClientSocket = io({
      auth: { token: loadSessionToken() ?? undefined },
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 600,
      reconnectionDelayMax: 4000,
      timeout: 8000,
    });
    socketRef.current = socket;

    socket.on('connect', () => setConnected(true));
    socket.on('disconnect', () => setConnected(false));
    socket.on('connect_error', () => {
      setConnected(false);
      pushToast('warning', 'No se pudo conectar con el servidor. Comprobando conexión…');
    });

    socket.on('session:ready', (payload) => {
      saveSessionToken(payload.token);
      socket.auth = { token: payload.token };
      setSession({ playerId: payload.playerId, token: payload.token });
      sessionRef.current = { playerId: payload.playerId, token: payload.token };
      socket.emit('profile:update', profileFromPreferences(preferencesRef.current), () => undefined);
      void loadMyEntitlements(payload.token);
      const pendingCode = initialRoomRef.current;
      if (pendingCode) {
        initialRoomRef.current = null;
        socket.emit('room:join', { code: pendingCode }, (result) => {
          if (!result.ok) pushToast('error', result.error);
        });
      }
    });

    socket.on('room:state', (next) => {
      const previous = roomRef.current;
      setRoomAndUrl(next);
      if (next) {
        const gameChanged = previous?.game?.id !== next.game?.id;
        const rematch = previous?.status === 'finished' && next.status === 'playing';
        if (!previous || previous.code !== next.code || gameChanged || rematch || next.status === 'lobby') {
          setAction(null);
        }
      } else {
        setAction(null);
      }
    });

    socket.on('lobby:rooms', (next) => setRooms(next));
    socket.on('game:action', (next) => {
      setAction(next);
      const soundOn = preferencesRef.current.sound;
      if (next.type === 'roll') playSound('roll', soundOn);
      else if (next.effect?.kind === 'win') playSound('win', soundOn);
      else if (next.animation?.kind === 'capture' || next.effect?.kind === 'capture') playSound('capture', soundOn);
      else if (next.animation?.kind === 'portal') playSound('portal', soundOn);
      else if (next.animation?.kind === 'crack') playSound('crack', soundOn);
      else if (next.animation?.kind === 'shield') playSound('shield', soundOn);
      else if (next.animation?.kind === 'wait') playSound('wait', soundOn);
      else if (next.animation?.kind === 'extra') playSound('extra', soundOn);
      else if (next.animation?.kind === 'goal') playSound('goal', soundOn);
      else if (next.animation?.kind === 'summon') playSound('effect', soundOn);
      else if (next.effect) playSound('effect', soundOn);
      else playSound('move', soundOn);
      if (next.type === 'move') {
        window.setTimeout(() => playSound('settle', preferencesRef.current.sound), 400);
      }
    });
    socket.on('notice', (notice) => pushToast(notice.tone, notice.message));
    socket.on('entitlements:update', (next) => {
      setEntitlements(next);
      setEntitlementsLoaded(true);
    });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
    };
  }, [loadMyEntitlements, pushToast, setRoomAndUrl]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [room?.code, room?.status]);

  useEffect(() => {
    if (!session || room?.status !== 'lobby') {
      setAiPassStatus(null);
      return;
    }
    void loadAiPassStatus(session.token);
  }, [session, room?.code, room?.status, loadAiPassStatus]);

  useEffect(() => {
    const state = checkoutStateRef.current;
    if (!session || !state || checkoutHandledRef.current) return undefined;
    checkoutHandledRef.current = true;
    if (state === 'ai-pass-cancel') {
      track('checkout_return', { status: 'cancel', ...getAttribution('shop') });
      pushToast('info', 'Compra del pase IA cancelada. No se ha cobrado nada.');
      stripCheckoutParam();
      return undefined;
    }
    if (state === 'ai-pass-success') {
      track('checkout_return', { status: 'success', ...getAttribution('shop') });
      pushToast('info', 'Pago del pase IA completado. Confirmando el acceso…');
      let attempts = 0;
      let cancelled = false;
      const poll = async () => {
        attempts += 1;
        try {
          const next = await getAiMatchPassStatus(session.token);
          if (cancelled) return;
          setAiPassStatus(next);
          if (next.status === 'active') {
            pushToast('success', 'Pase de partida IA activo. Ya puedes añadir hasta 2 agentes.');
            stripCheckoutParam();
            return;
          }
        } catch {
          // Seguimos reintentando mientras el webhook confirma el pago.
        }
        if (attempts < 7 && !cancelled) window.setTimeout(poll, 2000);
        else if (!cancelled) {
          pushToast('warning', 'El pase se está procesando. Recarga el lobby en unos segundos.');
          stripCheckoutParam();
        }
      };
      void poll();
      return () => {
        cancelled = true;
      };
    }
    if (state === 'cancel') {
      track('checkout_return', { status: 'cancel', ...getAttribution('shop') });
      pushToast('info', 'Pago cancelado. No se ha cobrado nada.');
      stripCheckoutParam();
      return undefined;
    }
    if (state !== 'success') {
      stripCheckoutParam();
      return undefined;
    }

    track('checkout_return', { status: 'success', ...getAttribution('shop') });
    pushToast('info', 'Pago completado. Confirmando el desbloqueo con el servidor…');
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      attempts += 1;
      try {
        const next = await getEntitlements(session.token);
        if (cancelled) return;
        setEntitlements(next);
        if (next.products.includes('voyager')) {
          pushToast('success', 'Pack Cosmético desbloqueado. Ya puedes usar sus avatares, dado, marco, efectos e insignia.');
          stripCheckoutParam();
          return;
        }
      } catch {
        // seguimos reintentando
      }
      if (attempts < 7 && !cancelled) {
        window.setTimeout(poll, 2000);
      } else if (!cancelled) {
        pushToast('warning', 'El pago se está procesando. Recarga la página en unos segundos para ver el desbloqueo.');
        stripCheckoutParam();
      }
    };
    void poll();
    return () => {
      cancelled = true;
    };
  }, [session, pushToast, stripCheckoutParam]);

  async function runAction(
    event: string,
    payload?: unknown,
    successMessage?: string,
  ): Promise<SocketAck | null> {
    const socket = socketRef.current;
    if (!socket?.connected) {
      pushToast('warning', 'Sin conexión con el servidor. Espera a que se restablezca.');
      return null;
    }
    setBusy(true);
    try {
      const result = await emitAck(socket, event, payload);
      if (!result.ok) {
        pushToast('error', result.error);
      } else if (successMessage) {
        pushToast('success', successMessage);
      }
      return result;
    } catch {
      pushToast('error', 'El servidor no respondió. Inténtalo de nuevo.');
      return null;
    } finally {
      setBusy(false);
    }
  }

  const createRoom = (settings: GameSettings) => {
    void runAction('room:create', settings);
  };

  const quickPlay = () => {
    void runAction('room:quick');
  };

  const joinRoom = (code: string) => {
    void runAction('room:join', { code });
  };

  const playWithBots = async () => {
    const socket = socketRef.current;
    if (!socket?.connected) {
      pushToast('warning', 'Sin conexión con el servidor.');
      return;
    }
    setBusy(true);
    try {
      const created = await emitAck(socket, 'room:create', {
        mode: 'adventure',
        effectsEnabled: true,
        maxPlayers: 4,
        visibility: 'private',
      });
      if (!created.ok) {
        pushToast('error', created.error);
        return;
      }
      for (let index = 0; index < 3; index += 1) {
        const added = await emitAck(socket, 'room:addBot');
        if (!added.ok) break;
      }
      const started = await emitAck(socket, 'room:start');
      if (!started.ok) pushToast('error', started.error);
    } catch {
      pushToast('error', 'No se pudo preparar la partida con bots.');
    } finally {
      setBusy(false);
    }
  };

  const startGame = () => {
    void runAction('room:start');
  };

  const updateSettings = (patch: Partial<GameSettings>) => {
    void runAction('room:settings', patch);
  };

  const addBot = () => {
    void runAction('room:addBot');
  };

  const removeBot = (botId: string) => {
    void runAction('room:removeBot', { playerId: botId });
  };

  const addAgent = async (provider: 'openai' | 'deepseek') => {
    const result = await runAction('room:addAgent', { provider });
    if (result?.ok && session) await loadAiPassStatus(session.token);
  };

  const removeAgent = (agentId: string) => {
    void runAction('room:removeAgent', { playerId: agentId });
  };

  const activateAiPass = async () => {
    if (!session) return;
    try {
      const next = await activateAiMatchPassTest(session.token);
      setAiPassStatus(next);
      pushToast('success', 'Pase de partida IA activado en modo staging.');
    } catch (error) {
      pushToast('error', error instanceof Error ? error.message : 'No se pudo activar el pase.');
    }
  };

  const buyAiPass = async () => {
    if (!session) return;
    try {
      const { url } = await createAiMatchPassCheckout(session.token);
      window.location.assign(url);
    } catch (error) {
      pushToast('error', error instanceof Error ? error.message : 'No se pudo iniciar el checkout test.');
    }
  };

  const leaveLobby = async () => {
    const result = await runAction('room:leave');
    if (result?.ok) {
      setRoomAndUrl(null);
      setAction(null);
    }
  };

  const forfeitGame = async () => {
    const result = await runAction('game:forfeit');
    if (result?.ok) {
      setRoomAndUrl(null);
      setAction(null);
    }
  };

  const roll = () => {
    void runAction('game:roll');
  };

  const move = (pieceId: string) => {
    void runAction('game:move', { pieceId });
  };

  const rematch = () => {
    void runAction('room:rematch');
  };

  const copyCode = async () => {
    if (!room) return;
    const ok = await copyToClipboard(room.code);
    pushToast(ok ? 'success' : 'error', ok ? 'Código copiado.' : 'No se pudo copiar el código.');
  };

  const copyLink = async () => {
    if (!room) return;
    const url = `${window.location.origin}/?sala=${room.code}`;
    const ok = await copyToClipboard(url);
    pushToast(ok ? 'success' : 'error', ok ? 'Enlace de invitación copiado.' : 'No se pudo copiar el enlace.');
  };

  const toggleSound = () => {
    const next = !preferences.sound;
    setPreferences((current) => ({ ...current, sound: next }));
    if (next) playSound('click', true);
  };

  const toggleMusic = () => {
    const next = !preferences.music;
    setPreferences((current) => ({ ...current, music: next }));
    if (next) startMusic(true, preferences.musicVolume);
    else stopMusic();
  };

  const openShop = useCallback((entry: CtaLocation = 'shop') => {
    const attribution = getAttribution(entry);
    applyAttributionToUrl(entry);
    track('pack_cta_click', {
      location: attribution.utm_content,
      utm_source: attribution.utm_source,
      utm_medium: attribution.utm_medium,
      utm_campaign: attribution.utm_campaign,
      utm_content: attribution.utm_content,
    });
    track('pack_modal_open', { entry });
    setShopOpen(true);
  }, []);

  const handleCheckout = async () => {
    if (!session) {
      pushToast('warning', 'Necesitas una sesión activa para comprar.');
      return;
    }
    if (!config?.paymentsEnabled) {
      pushToast('warning', 'El propietario todavía no ha configurado los pagos.');
      return;
    }
    const attribution = getAttribution('shop');
    track('checkout_click', {
      location: attribution.utm_content,
      utm_source: attribution.utm_source,
      utm_medium: attribution.utm_medium,
      utm_campaign: attribution.utm_campaign,
      utm_content: attribution.utm_content,
    });
    setCheckoutBusy(true);
    try {
      const { url } = await createCheckout(session.token, 'voyager', attribution);
      track('checkout_started', {
        location: attribution.utm_content,
        utm_source: attribution.utm_source,
        utm_medium: attribution.utm_medium,
        utm_campaign: attribution.utm_campaign,
        utm_content: attribution.utm_content,
      });
      window.location.assign(url);
    } catch (error) {
      pushToast('error', error instanceof Error ? error.message : 'No se pudo iniciar el pago.');
    } finally {
      setCheckoutBusy(false);
    }
  };

  const unlockedAvatars = FREE_AVATARS.filter((avatar): avatar is AvatarId => Boolean(avatar));
  if (entitlements.products.includes('voyager')) {
    unlockedAvatars.push(...entitlements.premiumAvatars);
  }
  const unlockedDice: DiceSkin[] = ['classic', 'glass', 'fire', 'tech'];
  if (entitlements.products.includes('voyager')) unlockedDice.push(...entitlements.premiumDice);

  const rulesInitialMode = room?.settings.mode ?? 'adventure';

  return (
    <>
      <div className="sky-decor" aria-hidden="true">
        <span className="sky-island sky-island--one" />
        <span className="sky-island sky-island--two" />
        <span className="sky-island sky-island--three" />
        <span className="sky-cloud sky-cloud--one" />
        <span className="sky-cloud sky-cloud--two" />
        <span className="sky-cloud sky-cloud--three" />
      </div>
      {!room && (
        <HomeScreen
          preferences={preferences}
          onPreferencesChange={setPreferences}
          rooms={rooms}
          connected={connected}
          busy={busy}
          config={config}
          entitlements={entitlements}
          unlockedAvatars={unlockedAvatars}
          onQuick={quickPlay}
          onCreate={createRoom}
          onJoin={joinRoom}
          onDemo={() => void playWithBots()}
          onRules={() => setRulesOpen(true)}
          onTutorial={() => setTutorialOpen(true)}
          onShop={openShop}
          onSettings={() => setSettingsOpen(true)}
          onRefresh={() => socketRef.current?.emit('lobby:request')}
        />
      )}

      {room?.status === 'lobby' && (
        <LobbyScreen
          room={room}
          playerId={session?.playerId ?? ''}
          busy={busy}
          onLeave={() => void leaveLobby()}
          onStart={startGame}
          onSettings={updateSettings}
          onAddBot={addBot}
          onRemoveBot={removeBot}
          aiPassStatus={aiPassStatus}
          testPurchaseEnabled={config?.aiMatchPassTestPurchaseEnabled ?? false}
          checkoutEnabled={config?.aiMatchPassCheckoutEnabled ?? false}
          availableAgentProviders={config?.availableAgentProviders ?? []}
          onActivateAiPass={() => void activateAiPass()}
          onBuyAiPass={() => void buyAiPass()}
          onAddAgent={(provider) => void addAgent(provider)}
          onRemoveAgent={removeAgent}
          onCopyCode={() => void copyCode()}
          onCopyLink={() => void copyLink()}
          onRules={() => setRulesOpen(true)}
          onSettingsModal={() => setSettingsOpen(true)}
          soundEnabled={preferences.sound}
          musicEnabled={preferences.music}
          onToggleSound={toggleSound}
          onToggleMusic={toggleMusic}
        />
      )}

      {room && room.status !== 'lobby' && (
        <GameScreen
          room={room}
          playerId={session?.playerId ?? ''}
          preferences={preferences}
          cosmetics={entitlements.cosmetics}
          action={action}
          busy={busy}
          connected={connected}
          onRoll={roll}
          onMove={move}
          onForfeit={() => void forfeitGame()}
          onRematch={rematch}
          onRules={() => setRulesOpen(true)}
          onSettings={() => setSettingsOpen(true)}
          onShop={() => openShop('shop')}
          onCopyCode={() => void copyCode()}
          onToggleSound={toggleSound}
          onToggleMusic={toggleMusic}
        />
      )}

      <TutorialModal
        open={tutorialOpen}
        onClose={() => {
          window.localStorage.setItem('altia.tutorial.seen', '1');
          setTutorialOpen(false);
        }}
      />
      <RulesModal open={rulesOpen} onClose={() => setRulesOpen(false)} initialMode={rulesInitialMode} />
      <SettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        preferences={preferences}
        onChange={setPreferences}
        unlockedAvatars={unlockedAvatars}
        unlockedDice={unlockedDice}
        onOpenShop={() => {
          setSettingsOpen(false);
          openShop('shop');
        }}
      />
      <ShopModal
        open={shopOpen}
        onClose={() => setShopOpen(false)}
        config={config}
        entitlements={entitlements}
        onCheckout={() => void handleCheckout()}
        checkoutBusy={checkoutBusy}
      />
      <Toasts toasts={toasts} onDismiss={dismissToast} />
    </>
  );
}
